/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

export function yearOnlyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') return null;

    const input = String(value).trim();
    if (!input) return null;

    // Must be exactly a 4-digit year
    const yearPattern = /^\d{4}$/;
    if (!yearPattern.test(input)) {
      return { invalidYear: true };
    }

    const year = parseInt(input, 10);
    const currentYear = new Date().getFullYear();
    if (year < 1900) {
      return { invalidYear: true };
    }
    if (year > currentYear) {
      return { futureYear: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-solar-energy-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './solar-energy-form.component.html',
  styleUrl: './solar-energy-form.component.scss',
})
export class SolarEnergyFormComponent implements OnInit {
  @Input() initialValue: any;
  @Input() creditScoreRange: string | undefined;
  @Output() formSubmit = new EventEmitter<any>();

  solarEnergyForm: FormGroup;
  submitAttempted = false;
  currentYear = new Date().getFullYear();
  creditScoreOptions = [
    'Below 580',
    '580 and above',
    '670 and above',
    '740 and above',
    '800 and above',
  ];

  private creditScoreRangeMap: Record<string, string> = {
    'Below 580': '<580',
    '580 and above': '580+',
    '670 and above': '670+',
    '740 and above': '740+',
    '800 and above': '800+',
  };

  constructor(private fb: FormBuilder) {
    this.solarEnergyForm = this.fb.group({
      interestedInSolar: ['', Validators.required],
      interestedInEnergyEfficiency: ['', Validators.required],
      buildingAge: ['', [yearOnlyValidator()]],
      buildingImprovements: [''],
      powerProviders: [''],
      interestedInBatteries: [''],
      canProvidePowerStatement: [''],
      hasElectricVehicles: [''],
      interestedInElectricVehicle: [''],
      creditScoreRange: [''],
    });
  }

  ngOnInit(): void {
    if (this.initialValue) {
      const patchData = { ...this.initialValue };

      // Handle credit score range reverse mapping
      if (patchData.creditScoreRange) {
        const reverseMap = Object.entries(this.creditScoreRangeMap).find(
          ([, value]) => value === patchData.creditScoreRange,
        );
        if (reverseMap) {
          patchData.creditScoreRange = reverseMap[0];
        }
      }

      this.solarEnergyForm.patchValue(patchData);
    }

    // Patch creditScoreRange if provided from parent (from business loan form)
    if (this.creditScoreRange) {
      let scoreVal = this.creditScoreRange;
      const reverseMap = Object.entries(this.creditScoreRangeMap).find(
        ([, value]) => value === this.creditScoreRange,
      );
      if (reverseMap) {
        scoreVal = reverseMap[0];
      }
      this.solarEnergyForm.patchValue({
        creditScoreRange: scoreVal,
      });
    }
  }

  get shouldShowCreditScoreQuestion(): boolean {
    // Only show if creditScoreRange hasn't been answered yet
    if (this.creditScoreRange) {
      return false;
    }

    // Show if user answered "Yes" to any of these questions
    const interestedInSolar =
      this.solarEnergyForm.get('interestedInSolar')?.value === true;
    const interestedInBatteries =
      this.solarEnergyForm.get('interestedInBatteries')?.value === 'yes';
    const interestedInElectricVehicle =
      this.solarEnergyForm.get('interestedInElectricVehicle')?.value === true;

    return (
      interestedInSolar || interestedInBatteries || interestedInElectricVehicle
    );
  }

  get hasErrors(): boolean {
    return this.submitAttempted && this.solarEnergyForm.invalid;
  }

  public onSubmitForm() {
    if (this.solarEnergyForm.valid) {
      const formValue = this.solarEnergyForm.value;

      // Map credit score range to API value
      const mappedCreditScoreRange = formValue.creditScoreRange
        ? (this.creditScoreRangeMap[formValue.creditScoreRange] ??
          formValue.creditScoreRange)
        : formValue.creditScoreRange;

      const payload = {
        ...formValue,
        creditScoreRange: mappedCreditScoreRange,
        // Ensure buildingAge is sent as a number (integer year)
        buildingAge:
          formValue.buildingAge !== '' && formValue.buildingAge !== null
            ? Number(formValue.buildingAge)
            : formValue.buildingAge,
      };

      this.formSubmit.emit(payload);
    } else {
      this.submitAttempted = true;
      this.markFormGroupTouched(this.solarEnergyForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
    });
  }
}
